import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, User, Eye, Tag, Clock, Share2,
  Facebook, Twitter, Linkedin, Copy, Check, Loader2
} from 'lucide-react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [relatedNews, setRelatedNews] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchNews();
  }, [slug]);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/news/public/${slug}`);
      setNews(response.data);
      
      // Fetch related news from same category
      const relatedResponse = await axios.get(`${API_URL}/api/news/public?category=${response.data.category}`);
      setRelatedNews(relatedResponse.data.filter(n => n.slug !== slug).slice(0, 3));
    } catch (err) {
      setError('Noticia no encontrada');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-PY', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = news?.title || '';

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !news) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-32 text-center">
          <h1 className="text-3xl font-bold text-zinc-900 mb-4">Noticia no encontrada</h1>
          <p className="text-zinc-600 mb-8">La noticia que buscás no existe o fue eliminada.</p>
          <Link 
            to="/nosotros/noticias"
            className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver a Noticias
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const embedUrl = getYouTubeEmbedUrl(news.video_url);

  return (
    <div className="min-h-screen bg-white" data-testid="news-detail-page">
      <Navigation />
      
      {/* Hero Image */}
      {news.image_url && (
        <div className="relative h-[400px] md:h-[500px] bg-zinc-900">
          <img 
            src={news.image_url} 
            alt={news.title}
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/50 to-transparent"></div>
        </div>
      )}

      {/* Content */}
      <article className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${news.image_url ? '-mt-48 relative z-10' : 'pt-32'}`}>
        {/* Back Button */}
        <Link 
          to="/nosotros/noticias"
          className={`inline-flex items-center gap-2 mb-6 transition-colors ${
            news.image_url ? 'text-white/80 hover:text-white' : 'text-zinc-600 hover:text-zinc-900'
          }`}
        >
          <ArrowLeft className="w-5 h-5" />
          Volver a Noticias
        </Link>

        {/* Category */}
        <span className="inline-block px-3 py-1 bg-emerald-500 text-white text-sm font-medium rounded-full mb-4">
          {news.category}
        </span>

        {/* Title */}
        <h1 className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-6 ${
          news.image_url ? 'text-white' : 'text-zinc-900'
        }`}>
          {news.title}
        </h1>

        {/* Meta */}
        <div className={`flex flex-wrap items-center gap-4 mb-8 text-sm ${
          news.image_url ? 'text-zinc-300' : 'text-zinc-500'
        }`}>
          <span className="flex items-center gap-1">
            <User className="w-4 h-4" />
            {news.author}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {formatDate(news.publish_date || news.created_at)}
          </span>
          <span className="flex items-center gap-1">
            <Eye className="w-4 h-4" />
            {news.views} vistas
          </span>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8">
          {/* Excerpt */}
          <p className="text-xl text-zinc-600 mb-8 font-medium leading-relaxed border-l-4 border-emerald-500 pl-6">
            {news.excerpt}
          </p>

          {/* Video if exists */}
          {embedUrl && (
            <div className="mb-8 rounded-xl overflow-hidden aspect-video">
              <iframe
                src={embedUrl}
                title={news.title}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          )}

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none prose-emerald prose-headings:text-zinc-900 prose-p:text-zinc-600 prose-a:text-emerald-600"
            dangerouslySetInnerHTML={{ __html: news.content }}
          />

          {/* Tags */}
          {news.tags && news.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-zinc-200">
              <div className="flex items-center gap-2 flex-wrap">
                <Tag className="w-5 h-5 text-zinc-400" />
                {news.tags.map((tag, idx) => (
                  <span 
                    key={idx}
                    className="px-3 py-1 bg-zinc-100 text-zinc-600 text-sm rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Share */}
          <div className="mt-8 pt-8 border-t border-zinc-200">
            <p className="text-sm font-medium text-zinc-700 mb-4 flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Compartir esta noticia
            </p>
            <div className="flex items-center gap-3">
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-[#1877F2] text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-[#1DA1F2] text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-3 bg-[#0A66C2] text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <button
                onClick={copyLink}
                className={`p-3 rounded-lg transition-all ${
                  copied 
                    ? 'bg-emerald-500 text-white' 
                    : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
                }`}
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Related News */}
        {relatedNews.length > 0 && (
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-zinc-900 mb-6">Noticias Relacionadas</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedNews.map((item) => (
                <Link 
                  key={item.id}
                  to={`/noticias/${item.slug}`}
                  className="group bg-white rounded-xl overflow-hidden shadow-sm border border-zinc-100 hover:shadow-lg transition-all"
                >
                  {item.image_url && (
                    <div className="h-40 overflow-hidden">
                      <img 
                        src={item.image_url} 
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <span className="text-xs text-emerald-600 font-medium">{item.category}</span>
                    <h3 className="font-semibold text-zinc-900 mt-1 line-clamp-2 group-hover:text-emerald-600 transition-colors">
                      {item.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default NewsDetailPage;
